import type { Vector2 } from 'three'
import { EventDispatcher, Vector3 } from 'three'

import { getEditControls } from '@/lib/three-d-editor/edit-controls'
import { pointerEventToVec2 } from '@/lib/three-d-editor/utils'
import { ControlEvent } from '@/lib/three-d-resources/controls/viewerControls'
import { LabelCreationVisualizer } from '@/lib/three-d-viewer/tools/LabelCreationVisualizer'
import type { Tool } from '@/lib/three-d-viewer/tools/ToolManager'
import type { TextLabel } from '~/lib/three-d-resources/labels-manager'
import type { HitResult, World } from '~/lib/three-d-resources/world'
import { DEFAULT_FIXED_SIZE, RELATIVE_DEFAULT_LABEL_FLAGPOLE_DISTANCE } from '@/lib/constants/labels'

interface LabelSelectEvent {
  'label-select': { textLabel?: TextLabel }
}

class TextLabelCreationTool extends EventDispatcher<LabelSelectEvent> implements Tool {
  id: 'label-creation' = 'label-creation'
  private _isCreatingLabel: boolean = false
  private _active = false
  private _visualizer: LabelCreationVisualizer | null = null
  private _lastHit: HitResult | null = null
  private _controls = getEditControls()

  constructor(private world: World) {
    super()
  }

  activate(lastMousePosition: Vector2 | undefined): void {
    if (!this.world.currentModel) {
      throw new Error('Scene view resource and model resource were not initialized')
    }
    if (lastMousePosition) this._lastHit = this.world.hitTest(lastMousePosition)
    this._active = true
    this._controls.addEventListener(ControlEvent.ViewChangedEvent, this.onViewChanged)
    this._visualizer = new LabelCreationVisualizer(this.world, ghost => ghost.quaternion.copy(this.world.currentCamera.quaternion))
    this._visualizer.initialize()
    if (lastMousePosition) this.onMouseMove(new MouseEvent('mousemove'), lastMousePosition)
  }

  deactivate(): void {
    // cleanup
    this._active = false
    this._controls.removeEventListener(ControlEvent.ViewChangedEvent, this.onViewChanged)

    this._isCreatingLabel = false
    this._visualizer?.dispose()
    this.world.targetVisualizer?.resetData()
  }

  private getCameraTargetDistance() {
    const target = new Vector3(...this._controls.target)
    return this.world.currentCamera.position.distanceTo(target)
  }

  onViewChanged = (): void => {
    if (!this._visualizer || !this._active) return
    const hit = this._lastHit
    if (hit && hit.normal) {
      const cameraTargetDistance = this.getCameraTargetDistance()
      this._visualizer.update(hit.point, hit.normal, cameraTargetDistance)
      this.world.targetVisualizer?.update(hit.point)
    }
  }

  onMouseMove(_: MouseEvent, mousePosition: Vector2) {
    if (!this._visualizer || !this._active) return
    const hit = this.world.hitTest(mousePosition)

    if (hit && hit.normal && !hit.billboard && !hit.label) {
      this._lastHit = hit
      const cameraTargetDistance = this.getCameraTargetDistance()
      this._visualizer.update(hit.point, hit.normal, cameraTargetDistance)
      this.world.targetVisualizer?.update(hit.point)
      if (!hit.label && !this._isCreatingLabel) {
        this._showVisualizers()
      }
    } else {
      this._hideVisualizers()
    }
  }

  onMouseDown(_e: MouseEvent) {}

  onMouseDownCapture(_e: MouseEvent) {}

  onMouseUp(_e: MouseEvent) {}

  onMouseLeave(_e: MouseEvent): void {
    this._hideVisualizers()
  }

  onClick(e: MouseEvent): void {
    // Only trigger if LMB
    if (e.button !== 0 || !this._visualizer || !this.world.currentModel || !this.world.labelsManager) {
      return
    }

    const mousePosition = pointerEventToVec2(e)
    const labelTextSize = Math.round(this._visualizer.getLabelTextSize())
    const hit = this.world.hitTest(mousePosition)
    if (!hit || !hit.normal || hit.billboard) {
      return
    }

    e.stopPropagation()

    if (this._isCreatingLabel) {
      this._hideVisualizers()
    }

    this._isCreatingLabel = true

    const textScale = this.world.labelsManager.globalTextScale
    hit.normal.setY(-hit.normal.y)
    hit.normal.multiplyScalar(textScale * labelTextSize * RELATIVE_DEFAULT_LABEL_FLAGPOLE_DISTANCE)

    const textLabel = {
      id: `local-label-${Date.now()}`,
      modelId: this.world.currentModel.id,
      point: hit.point,
      offsetX: hit.normal.x,
      offsetY: hit.normal.y,
      offsetZ: hit.normal.z,
      content: '',
      visible: true,
      isNewlyCreated: true,
      isEditing: true,
      nodeId: this.world.currentModel?.apiLayersById.get(hit.nodeId)!.parent_id ?? 0,
      flagpoleVisible: true,
      flagpoleIndex: this._visualizer.getFlagpoleIndex(),
      textSize: labelTextSize,
      autoScaleSize: DEFAULT_FIXED_SIZE,
      autoScale: false,
      refreshKey: Date.now(),
    }

    this.world.labelsManager.addLabel(textLabel)

    this.dispatchEvent({ type: 'label-select', textLabel })
  }

  private _showVisualizers() {
    this._visualizer!.show()
    this.world.targetVisualizer?.show()
  }

  private _hideVisualizers() {
    this._visualizer!.hide()
    this.world.targetVisualizer?.hide()
  }
}

export { TextLabelCreationTool }
